import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import './RankfulAccountDeletion.scss';

function RankfulAccountDeletion() {
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [reason, setReason] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    setError(null);

    try {
      const response = await fetch('/api/account-deletion', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ app: 'rankful', email, username, reason }),
      });
      const data = await response.json().catch(() => ({}));

      if (!response.ok || data?.error) {
        setError(data?.error || 'Something went wrong. Please try again.');
        setLoading(false);
        return;
      }
      setSubmitted(true);
    } catch {
      setError('Could not reach the server. Please check your connection and try again.');
    }
    setLoading(false);
  };

  return (
    <>
      <Helmet>
        <title>Account Deletion Request | Rankful</title>
        <meta name="description" content="Request deletion of your Rankful app account and associated data." />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <section className="rankful-deletion">
        <div className="rankful-deletion-container container">
          <div className="rankful-deletion-header">
            <h1 className="rankful-deletion-title">Rankful Account Deletion</h1>
            <p className="rankful-deletion-subtitle">
              Request deletion of your account and associated data
            </p>
          </div>

          {submitted ? (
            <div className="rankful-deletion-success">
              <h2>Request Received</h2>
              <p>
                Your account deletion request has been submitted successfully.
              </p>
              <p>
                We will process your request within 30 days and send confirmation
                to your email address.
              </p>
            </div>
          ) : (
            <form className="rankful-deletion-form" onSubmit={handleSubmit}>
              <div className="rankful-deletion-info">
                <h2>What happens when you delete your account?</h2>
                <ul>
                  <li>Your profile and username will be permanently deleted</li>
                  <li>Your game history and scores will be removed</li>
                  <li>Your default game settings will be erased</li>
                  <li>This action cannot be undone</li>
                </ul>
              </div>

              <div className="rankful-deletion-field">
                <label htmlFor="username">Username in Rankful *</label>
                <input
                  type="text"
                  id="username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                  placeholder="Enter your Rankful username"
                />
              </div>

              <div className="rankful-deletion-field">
                <label htmlFor="email">Email Address *</label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  placeholder="Enter the email associated with your account"
                />
              </div>

              <div className="rankful-deletion-field">
                <label htmlFor="reason">Reason for deletion (optional)</label>
                <textarea
                  id="reason"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Let us know why you're leaving (optional)"
                  rows={3}
                />
              </div>

              {error && <p className="rankful-deletion-error">{error}</p>}

              <button
                type="submit"
                className="rankful-deletion-submit"
                disabled={loading}
              >
                {loading ? 'Submitting...' : 'Submit Deletion Request'}
              </button>
            </form>
          )}
        </div>
      </section>
    </>
  );
}

export default RankfulAccountDeletion;
